/**
 * SpotGPT Service - Centralized API and utility functions for SpotGPT chat functionality
 * Base URL: https://phone.getscreened.in/spotgpt
 * 
 * Updated to support new session-based API with:
 * - Session management (start, list, delete, get messages)
 * - Streaming with token/tool events
 * - Quick actions (matching, select, filter)
 * - Message limits (20 per session)
 */

const BASE_URL = 'https://phone.getscreened.in/spotgpt';

// ============================================================================
// SESSION MANAGEMENT
// ============================================================================

/**
 * Start or resume a session
 * @param {string} email - User's email (required for session ownership)
 * @param {string} mode - Chat mode: 'normal', 'advanced', or 'spot'
 * @param {string} jdId - Job Description ID (only for 'spot' mode)
 * @param {boolean} forceNew - Force create new session even if one exists
 * @returns {Promise<Object>} Session info: {session_id, is_new, message_count, remaining_messages, mode, jd_id, title}
 */
export const startSession = async (email, mode = 'spot', jdId = null, forceNew = false) => {
  try {
    const requestBody = {
      email,
      mode,
      force_new: forceNew
    };

    if (mode === 'spot' && jdId) {
      requestBody.jd_id = jdId;
    }

    console.log('📦 SpotGPT Start Session:', requestBody);

    const response = await fetch(`${BASE_URL}/session/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ SpotGPT Start Session Error:', errorText);
      throw new Error(errorText || 'Failed to start session');
    }

    const data = await response.json();
    console.log('✅ Session started:', data);

    return {
      sessionId: data.session_id,
      isNew: data.is_new,
      messageCount: data.message_count || 0,
      remainingMessages: data.remaining_messages || 20,
      mode: data.mode,
      jdId: data.jd_id,
      title: data.title || 'New Conversation'
    };
  } catch (error) {
    console.error('💥 SpotGPT start session failed:', error);
    throw error;
  }
};

/**
 * List all sessions for a user
 * @param {string} email - User's email
 * @param {string} mode - Optional mode filter
 * @param {string} jdId - Optional JD ID filter
 * @returns {Promise<Array>} Array of sessions
 */
export const listSessions = async (email, mode = null, jdId = null) => {
  try {
    const params = new URLSearchParams({ email });
    if (mode) params.append('mode', mode);
    if (jdId) params.append('jd_id', jdId);

    console.log('📦 SpotGPT List Sessions:', { email, mode, jdId });

    const response = await fetch(`${BASE_URL}/sessions?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ SpotGPT List Sessions Error:', errorText);
      throw new Error(errorText || 'Failed to list sessions');
    }

    const data = await response.json();
    console.log('✅ Sessions listed:', data.sessions?.length || 0);

    return data.sessions || [];
  } catch (error) {
    console.error('💥 SpotGPT list sessions failed:', error);
    throw error;
  }
};

/**
 * Get messages for a specific session
 * @param {string} sessionId - Session ID
 * @param {string} email - User's email (for verification)
 * @returns {Promise<Object>} Session with messages
 */
export const getSessionMessages = async (sessionId, email) => {
  try {
    console.log('📦 SpotGPT Get Session Messages:', { sessionId, email });

    const response = await fetch(`${BASE_URL}/sessions/${sessionId}/messages?email=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ SpotGPT Get Messages Error:', errorText);
      throw new Error(errorText || 'Failed to get session messages');
    }

    const data = await response.json();
    console.log('✅ Messages retrieved:', data.messages?.length || 0);

    return {
      sessionId: data.session_id,
      messages: data.messages || [],
      messageCount: data.message_count || 0,
      remainingMessages: data.remaining_messages || 0,
      mode: data.mode,
      jdId: data.jd_id,
      title: data.title
    };
  } catch (error) {
    console.error('💥 SpotGPT get session messages failed:', error);
    throw error;
  }
};

/**
 * Delete a session
 * @param {string} sessionId - Session ID
 * @param {string} email - User's email (for verification)
 * @returns {Promise<boolean>} Success status
 */
export const deleteSession = async (sessionId, email) => {
  try {
    console.log('📦 SpotGPT Delete Session:', { sessionId, email });

    const response = await fetch(`${BASE_URL}/sessions/${sessionId}?email=${encodeURIComponent(email)}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ SpotGPT Delete Session Error:', errorText);
      throw new Error(errorText || 'Failed to delete session');
    }

    console.log('✅ Session deleted:', sessionId);
    return true;
  } catch (error) {
    console.error('💥 SpotGPT delete session failed:', error);
    throw error;
  }
};

// ============================================================================
// CHAT MESSAGING
// ============================================================================

/**
 * Send a chat message with streaming support
 * @param {string} sessionId - Session ID
 * @param {string} message - User's message
 * @param {string} mode - Chat mode: 'normal', 'advanced', or 'spot'
 * @param {string} jdId - Job Description ID (for spot mode)
 * @param {Function} onToken - Callback for token events
 * @param {Function} onToolStart - Callback for tool start events
 * @param {Function} onToolEnd - Callback for tool end events
 * @param {Function} onDone - Callback for completion
 * @param {Function} onError - Callback for errors
 * @param {Function} onSessionLimit - Callback for session limit reached
 * @returns {Promise<void>}
 */
export const sendChatMessageStreaming = async (
  sessionId,
  message,
  mode = 'spot',
  jdId = null,
  { onToken, onToolStart, onToolEnd, onDone, onError, onSessionLimit } = {}
) => {
  try {
    const requestBody = {
      session_id: sessionId,
      message,
      mode,
      stream: true
    };

    if (mode === 'spot' && jdId) {
      requestBody.jd_id = jdId;
    }

    console.log('📦 SpotGPT Chat Request:', requestBody);

    const response = await fetch(`${BASE_URL}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ SpotGPT Chat Error:', errorText);
      throw new Error(errorText || 'Chat request failed');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullResponse = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6).trim();
            
            if (jsonStr === '[DONE]') {
              console.log('✅ Stream [DONE] marker received');
              continue;
            }
            
            try {
              const data = JSON.parse(jsonStr);
              
              // Handle token event
              if (data.token) {
                fullResponse += data.token;
                if (onToken) onToken(data.token, fullResponse);
              }
              
              // Handle tool start event
              if (data.tool_start) {
                console.log('🔧 Tool started:', data.tool_start);
                if (onToolStart) onToolStart(data.tool_start);
              }
              
              // Handle tool end event
              if (data.tool_end) {
                console.log('✅ Tool ended:', data.tool_end);
                if (onToolEnd) onToolEnd(data.tool_end);
              }
              
              // Handle done event (with full response)
              if (data.done === true) {
                console.log('🏁 Stream complete:', {
                  responseLength: data.response?.length,
                  messageCount: data.message_count,
                  remainingMessages: data.remaining_messages
                });
                
                if (onDone) {
                  onDone({
                    response: data.response || fullResponse,
                    sessionId: data.session_id,
                    mode: data.mode,
                    messageCount: data.message_count,
                    remainingMessages: data.remaining_messages,
                    tokensUsed: data.tokens_used,
                    cost: data.cost,
                    candidatesReferringTo: data.candidates_referring_to || []
                  });
                }
              }
              
              // Handle session limit error
              if (data.error === 'session_limit_reached') {
                console.warn('⚠️ Session limit reached:', data);
                if (onSessionLimit) {
                  onSessionLimit({
                    message: data.message,
                    currentCount: data.current_count,
                    sessionId: data.session_id,
                    actionRequired: data.action_required
                  });
                }
              }
              
              // Handle other errors
              if (data.error && data.error !== 'session_limit_reached') {
                console.error('❌ Stream error:', data.error);
                if (onError) onError(data.error);
              }
              
            } catch (e) {
              if (jsonStr && jsonStr.trim() !== '') {
                console.warn('⚠️ Could not parse:', jsonStr);
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    console.error('💥 SpotGPT chat streaming failed:', error);
    if (onError) onError(error.message);
    throw error;
  }
};

/**
 * Send a chat message without streaming (for simple use cases)
 * @param {string} sessionId - Session ID
 * @param {string} message - User's message
 * @param {string} mode - Chat mode
 * @param {string} jdId - Job Description ID
 * @returns {Promise<Object>} Chat response
 */
export const sendChatMessage = async (sessionId, message, mode = 'spot', jdId = null) => {
  try {
    const requestBody = {
      session_id: sessionId,
      message,
      mode,
      stream: false
    };

    if (mode === 'spot' && jdId) {
      requestBody.jd_id = jdId;
    }

    console.log('📦 SpotGPT Chat (no stream):', requestBody);

    const response = await fetch(`${BASE_URL}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Chat request failed');
    }

    const data = await response.json();
    
    return {
      response: data.response || data.answer || '',
      sessionId: data.session_id,
      messageCount: data.message_count,
      remainingMessages: data.remaining_messages,
      candidatesReferringTo: data.candidates_referring_to || []
    };
  } catch (error) {
    console.error('💥 SpotGPT chat failed:', error);
    throw error;
  }
};

// ============================================================================
// QUICK ACTIONS
// ============================================================================

/**
 * Run matching engine for a job
 * @param {string} jobId - Job/JD ID
 * @param {boolean} autoFilter - Whether to auto-filter candidates
 * @returns {Promise<Object>} Matching results
 */
export const runMatchingEngine = async (jobId, autoFilter = false) => {
  try {
    console.log('📦 SpotGPT Run Matching:', { jobId, autoFilter });

    const response = await fetch(`${BASE_URL}/quick-actions/run-matching`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        job_id: jobId,
        auto_filter: autoFilter
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Run matching failed');
    }

    const data = await response.json();
    console.log('✅ Matching complete:', data);
    return data;
  } catch (error) {
    console.error('💥 Run matching failed:', error);
    throw error;
  }
};

/**
 * Select a candidate for screening
 * @param {string} jobId - Job/JD ID
 * @param {string} candidateId - Candidate ID
 * @returns {Promise<Object>} Selection result
 */
export const selectCandidate = async (jobId, candidateId) => {
  try {
    console.log('📦 SpotGPT Select Candidate:', { jobId, candidateId });

    const response = await fetch(`${BASE_URL}/quick-actions/select-candidate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        job_id: jobId,
        candidate_id: candidateId
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Select candidate failed');
    }

    const data = await response.json();
    console.log('✅ Candidate selected:', data);
    return data;
  } catch (error) {
    console.error('💥 Select candidate failed:', error);
    throw error;
  }
};

/**
 * Filter a candidate
 * @param {string} jobId - Job/JD ID
 * @param {string} candidateId - Candidate ID
 * @returns {Promise<Object>} Filter result
 */
export const filterCandidate = async (jobId, candidateId) => {
  try {
    console.log('📦 SpotGPT Filter Candidate:', { jobId, candidateId });

    const response = await fetch(`${BASE_URL}/quick-actions/filter-candidate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        job_id: jobId,
        candidate_id: candidateId
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Filter candidate failed');
    }

    const data = await response.json();
    console.log('✅ Candidate filtered:', data);
    return data;
  } catch (error) {
    console.error('💥 Filter candidate failed:', error);
    throw error;
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format markdown text for display
 * @param {string} text - Text to format
 * @returns {string} HTML formatted text
 */
export const formatMarkdown = (text) => {
  if (!text) return '';
  
  let formatted = text
    // Code blocks with syntax highlighting
    .replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre class="code-block"><code class="language-$1">$2</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>')
    // Bold
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    // Headers
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Lists
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>')
    // Links with video support
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
      if (url.includes('?t=') || url.includes('#skill-')) {
        const isVideo = url.includes('?t=');
        const className = isVideo ? 'skill-video-link has-video' : 'skill-video-link no-video';
        return `<a href="${url}" class="${className}" data-skill="${text}" onclick="event.preventDefault();">${isVideo ? '🎥 ' : ''}${text}</a>`;
      }
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    })
    // Line breaks
    .replace(/\n/g, '<br>');
  
  // Wrap consecutive list items
  formatted = formatted.replace(/(<li>.*<\/li>)(?:<br>)?(<li>)/g, '$1$2');
  
  return formatted;
};

/**
 * Truncate text with word limit
 * @param {string} text - Text to truncate
 * @param {number} wordLimit - Maximum number of words
 * @returns {string} Truncated text
 */
export const truncateText = (text, wordLimit) => {
  if (!text) return '';
  const words = text.split(' ');
  if (words.length <= wordLimit) {
    return text;
  }
  return `${words.slice(0, wordLimit).join(' ')}...`;
};

/**
 * Generate a unique conversation ID
 * @returns {string} Unique ID
 */
export const generateConversationId = () => {
  return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Extract topic from the first user message
 * @param {string} message - First message
 * @param {number} maxLength - Maximum length for topic
 * @returns {string} Topic string
 */
export const extractTopic = (message, maxLength = 50) => {
  if (!message) return 'New Conversation';
  
  const cleaned = message.trim();
  if (cleaned.length <= maxLength) {
    return cleaned;
  }
  
  return cleaned.substring(0, maxLength) + '...';
};

/**
 * Format message count display
 * @param {number} messageCount - Current message count
 * @param {number} remainingMessages - Remaining messages
 * @param {number} maxMessages - Maximum messages (default 20)
 * @returns {string} Formatted string like "5/20"
 */
export const formatMessageCount = (messageCount, remainingMessages, maxMessages = 20) => {
  const current = messageCount || (maxMessages - (remainingMessages || maxMessages));
  return `${current}/${maxMessages}`;
};

/**
 * Check if session is near limit
 * @param {number} remainingMessages - Remaining messages
 * @param {number} threshold - Warning threshold (default 3)
 * @returns {boolean} True if near limit
 */
export const isNearSessionLimit = (remainingMessages, threshold = 3) => {
  return remainingMessages !== undefined && remainingMessages <= threshold;
};

/**
 * Check if session has reached limit
 * @param {number} remainingMessages - Remaining messages
 * @returns {boolean} True if at limit
 */
export const isAtSessionLimit = (remainingMessages) => {
  return remainingMessages !== undefined && remainingMessages <= 0;
};

// ============================================================================
// LOCAL STORAGE HELPERS (for backward compatibility)
// ============================================================================

/**
 * Store current session info in localStorage
 * @param {string} key - Storage key
 * @param {Object} sessionInfo - Session information
 */
export const storeSessionInfo = (key, sessionInfo) => {
  try {
    localStorage.setItem(key, JSON.stringify(sessionInfo));
  } catch (error) {
    console.error('Failed to store session info:', error);
  }
};

/**
 * Get stored session info from localStorage
 * @param {string} key - Storage key
 * @returns {Object|null} Session info or null
 */
export const getStoredSessionInfo = (key) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to get session info:', error);
    return null;
  }
};

/**
 * Clear stored session info
 * @param {string} key - Storage key
 */
export const clearStoredSessionInfo = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to clear session info:', error);
  }
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const spotGptService = {
  // Session management
  startSession,
  listSessions,
  getSessionMessages,
  deleteSession,
  
  // Chat
  sendChatMessage,
  sendChatMessageStreaming,
  
  // Quick actions
  runMatchingEngine,
  selectCandidate,
  filterCandidate,
  
  // Utilities
  formatMarkdown,
  truncateText,
  generateConversationId,
  extractTopic,
  formatMessageCount,
  isNearSessionLimit,
  isAtSessionLimit,
  
  // Storage helpers
  storeSessionInfo,
  getStoredSessionInfo,
  clearStoredSessionInfo
};

export default spotGptService;
